import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { formatMoney, initials } from '@dezza/shared';
import { cn } from '../lib/cn';

// ---------------------------------------------------------------- Spinner
export function Spinner({ className }: { className?: string }) {
  return (
    <svg className={cn('size-5 animate-spin', className)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity=".2" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function PageLoader({ label = 'Carregando...' }: { label?: string }) {
  return (
    <div role="status" className="flex min-h-[40vh] animate-fade-in flex-col items-center justify-center gap-3 text-ink-3">
      <Spinner className="size-6 text-primary" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

// ---------------------------------------------------------------- Card
export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Clicável: cursor de mão, leve elevação no hover e "apertar" no toque. */
  interactive?: boolean;
  /** Só a elevação no hover (sem cursor de clique). */
  lift?: boolean;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card({ className, interactive, lift, ...props }, ref) {
  return (
    <div
      ref={ref}
      className={cn(
        'rounded-card border border-line bg-surface shadow-card',
        (interactive || lift) &&
          'transition-[box-shadow,translate,border-color,scale] duration-200 ease-standard hover:-translate-y-px hover:border-line-strong hover:shadow-raised motion-reduce:hover:translate-y-0',
        interactive && 'cursor-pointer active:translate-y-0 active:scale-[0.995]',
        className,
      )}
      {...props}
    />
  );
});

export function CardHeader({
  className,
  title,
  subtitle,
  action,
  eyebrow,
}: {
  className?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  eyebrow?: ReactNode;
}) {
  return (
    <div className={cn('flex items-start justify-between gap-3 px-4 pt-4 sm:px-5 sm:pt-5', className)}>
      <div className="min-w-0">
        {eyebrow ? <div className="eyebrow mb-1.5 text-ink-3">{eyebrow}</div> : null}
        <h3 className="text-[15px] font-semibold tracking-[-0.01em] text-ink">{title}</h3>
        {subtitle ? <p className="mt-0.5 text-sm text-ink-2">{subtitle}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

export function CardBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('p-4 sm:p-5', className)} {...props} />;
}

export function CardFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex items-center gap-2 border-t border-line px-4 py-3 sm:px-5', className)} {...props} />;
}

// ---------------------------------------------------------------- Badge
export const badgeVariants = cva(
  'inline-flex items-center gap-1 whitespace-nowrap rounded-full font-semibold leading-none [&_svg]:size-3 [&_svg]:shrink-0',
  {
    variants: {
      tone: {
        neutral: 'bg-surface-2 text-ink-2 ring-1 ring-inset ring-line',
        primary: 'bg-primary-soft text-primary-strong',
        success: 'bg-success-soft text-success',
        solid: 'bg-primary text-on-primary',
        warning: 'bg-warning-soft text-warning',
        danger: 'bg-danger-soft text-danger',
        'danger-solid': 'bg-danger-solid text-on-danger',
        info: 'bg-info-soft text-info',
        new: 'bg-accent text-on-accent',
        accent: 'bg-accent text-on-accent',
        gold: 'bg-gold text-on-gold',
        dark: 'bg-dark text-on-dark ring-1 ring-inset ring-white/10',
        outline: 'text-ink-2 ring-1 ring-inset ring-line-strong',
      },
      size: {
        sm: 'px-1.5 py-[3px] text-[10.5px] uppercase tracking-[0.06em]',
        md: 'px-2.5 py-1 text-xs',
      },
    },
    defaultVariants: { tone: 'neutral', size: 'md' },
  },
);

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {
  /** Ponto de status antes do texto (a cor segue o tom). */
  dot?: boolean;
}

export function Badge({ className, tone, size, dot, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ tone, size }), className)} {...props}>
      {dot ? <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-current" /> : null}
      {children}
    </span>
  );
}

/** Selo "Novo" (lima com texto tinta) usado no menu e em recursos recém-lançados. */
export function NewBadge({ className }: { className?: string }) {
  return (
    <Badge tone="new" size="sm" className={className}>
      Novo
    </Badge>
  );
}

// ---------------------------------------------------------------- Avatar
/** Fundos das iniciais: todos com texto branco >= 4,5:1. */
const AVATAR_COLORS = ['#6B4EFF', '#2457D6', '#0B7D57', '#B8236B', '#B4460C', '#475266', '#8A3FD1', '#0E7490'];

function hashColor(name: string): string {
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length]!;
}

export function Avatar({ name, src, size = 40, className }: { name: string; src?: string | null; size?: number; className?: string }) {
  const style = { width: size, height: size, fontSize: Math.max(11, size * 0.38) };
  if (src) {
    return <img src={src} alt="" style={style} className={cn('shrink-0 rounded-full bg-surface-2 object-cover ring-1 ring-line', className)} />;
  }
  return (
    <span
      aria-hidden="true"
      style={{ ...style, backgroundColor: hashColor(name) }}
      className={cn('inline-flex shrink-0 items-center justify-center rounded-full font-semibold tracking-[-0.02em] text-white', className)}
    >
      {initials(name)}
    </span>
  );
}

// ---------------------------------------------------------------- Skeleton
export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden="true" className={cn('shimmer rounded-lg bg-ink/[0.07]', className)} />;
}

export function ListSkeleton({ rows = 6, avatar = true }: { rows?: number; avatar?: boolean }) {
  return (
    <div className="divide-y divide-line" role="status" aria-label="Carregando">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3">
          {avatar ? <Skeleton className="size-11 rounded-xl" /> : null}
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3.5 w-1/2" />
            <Skeleton className="h-3 w-1/3" />
          </div>
          <Skeleton className="h-4 w-16" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- EmptyState
export function EmptyState({
  illustration,
  icon,
  title,
  description,
  actions,
  className,
  compact,
}: {
  /** Ilustração da marca (IllustrationEmptyBox...) ou qualquer imagem. */
  illustration?: ReactNode;
  /** Alternativa leve à ilustração: ícone num quadrado suave. */
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
  compact?: boolean;
}) {
  return (
    <div className={cn('mx-auto flex max-w-md animate-fade-in flex-col items-center px-6 text-center', compact ? 'py-8' : 'py-12', className)}>
      {illustration ? <div className={cn('mb-5', compact ? 'w-32' : 'w-44')}>{illustration}</div> : null}
      {!illustration && icon ? (
        <div className="mb-4 flex size-12 items-center justify-center rounded-2xl bg-primary-soft text-primary-strong ring-1 ring-inset ring-primary/10 [&_svg]:size-6">
          {icon}
        </div>
      ) : null}
      <h3 className="text-[17px] font-semibold tracking-[-0.015em] text-ink">{title}</h3>
      {description ? <p className="mt-1.5 text-[15px] leading-relaxed text-ink-2">{description}</p> : null}
      {actions ? <div className="mt-6 flex w-full flex-col gap-2 sm:w-auto sm:flex-row">{actions}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------- Price
export function Price({
  cents,
  oldCents,
  size = 'md',
  className,
  hidden,
}: {
  cents: number;
  oldCents?: number | null;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  /** Valores ocultos (botão do olho no início). */
  hidden?: boolean;
}) {
  const sizes = { sm: 'text-sm', md: 'text-base', lg: 'text-xl tracking-[-0.02em]', xl: 'text-3xl tracking-[-0.03em]' };
  if (hidden) return <span className={cn('tabular font-semibold tracking-widest', sizes[size], className)}>R$ ••••</span>;
  return (
    <span className={cn('inline-flex flex-wrap items-baseline gap-x-2', className)}>
      {oldCents && oldCents > cents ? (
        <span className="tabular text-[0.8em] text-ink-3 line-through decoration-danger/70">{formatMoney(oldCents)}</span>
      ) : null}
      <span className={cn('tabular font-semibold', sizes[size])}>{formatMoney(cents)}</span>
    </span>
  );
}

// ---------------------------------------------------------------- Delta (variação)
/** Variação percentual com seta e cor (nunca só cor: sempre seta e sinal). */
export function Delta({
  value,
  invert,
  suffix,
  className,
}: {
  value: number;
  /** Para custos: cair é bom (verde). */
  invert?: boolean;
  suffix?: ReactNode;
  className?: string;
}) {
  const up = value >= 0;
  const good = invert ? !up : up;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={cn('inline-flex items-center gap-1 text-xs font-semibold', good ? 'text-success' : 'text-danger', className)}>
      <Icon className="size-3.5 shrink-0" strokeWidth={2.5} aria-hidden="true" />
      <span className="tabular">
        {up ? '+' : '−'}
        {Math.abs(value).toLocaleString('pt-BR', { maximumFractionDigits: Math.abs(value) < 10 ? 1 : 0 })}%
      </span>
      {suffix ? <span className="font-medium text-ink-3">{suffix}</span> : null}
    </span>
  );
}

// ---------------------------------------------------------------- Stat
export function Stat({
  label,
  value,
  hint,
  delta,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  /** Variação percentual vs. período anterior */
  delta?: number | null;
  className?: string;
}) {
  return (
    <div className={cn('min-w-0', className)}>
      <div className="text-[13px] font-medium text-ink-2">{label}</div>
      <div className="tabular mt-1 truncate text-2xl font-semibold tracking-[-0.03em] text-ink">{value}</div>
      {(delta !== undefined && delta !== null) || hint ? (
        <div className="mt-1 flex items-center gap-2 text-xs">
          {delta !== undefined && delta !== null ? <Delta value={delta} /> : null}
          {hint ? <span className="text-ink-3">{hint}</span> : null}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- Progress (uso do plano)
const PROGRESS_TONES = {
  primary: 'bg-primary',
  success: 'bg-success-solid',
  warning: 'bg-warning-solid',
  danger: 'bg-danger-solid',
  accent: 'bg-accent',
} as const;

export function Progress({
  value,
  max,
  className,
  label,
  tone,
  size = 'md',
}: {
  value: number;
  max: number | null;
  className?: string;
  label?: string;
  /** Força uma cor. Sem tom: violeta, alerta a partir de 80% e erro em 100%. */
  tone?: keyof typeof PROGRESS_TONES;
  size?: 'sm' | 'md';
}) {
  const pct = max ? Math.min(100, Math.round((value / max) * 100)) : 0;
  const auto = pct >= 100 ? 'danger' : pct >= 80 ? 'warning' : 'primary';
  const fill = PROGRESS_TONES[tone ?? (max === null ? 'primary' : auto)];
  return (
    <div className={className}>
      {label ? (
        <div className="mb-1.5 flex justify-between gap-3 text-sm">
          <span className="text-ink-2">{label}</span>
          <span className="tabular font-medium text-ink">{max === null ? `${value} / ilimitado` : `${value} / ${max}`}</span>
        </div>
      ) : null}
      <div
        className={cn('overflow-hidden rounded-full bg-ink/[0.08]', size === 'sm' ? 'h-1.5' : 'h-2')}
        role="progressbar"
        aria-label={label}
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max ?? undefined}
      >
        <div
          className={cn('h-full rounded-full transition-[width] duration-500 ease-snappy', fill, max === null && 'w-[6%]')}
          style={max === null ? undefined : { width: `${Math.max(pct, 2)}%` }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Diversos
export function Kbd({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <kbd
      className={cn(
        'inline-flex h-5 min-w-5 items-center justify-center rounded-md border border-line-strong bg-surface px-1.5 font-mono text-[11px] font-medium text-ink-2 shadow-[0_1px_0_var(--color-line-strong)]',
        className,
      )}
    >
      {children}
    </kbd>
  );
}

export function Divider({ className, label }: { className?: string; label?: string }) {
  if (!label) return <hr className={cn('border-line', className)} />;
  return (
    <div className={cn('eyebrow flex items-center gap-3 text-ink-3', className)}>
      <span className="h-px flex-1 bg-line" />
      {label}
      <span className="h-px flex-1 bg-line" />
    </div>
  );
}

export function SectionTitle({ children, action, className }: { children: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex items-center justify-between px-1 pb-2 pt-5', className)}>
      <h2 className="eyebrow text-ink-3">{children}</h2>
      {action}
    </div>
  );
}

/** Miniatura de produto com fallback de iniciais. */
export function Thumb({
  src,
  name,
  size = 48,
  className,
  rounded = 'xl',
}: {
  src?: string | null;
  name: string;
  size?: number;
  className?: string;
  rounded?: 'lg' | 'xl' | '2xl';
}) {
  const r = { lg: 'rounded-lg', xl: 'rounded-xl', '2xl': 'rounded-2xl' }[rounded];
  if (src) {
    return (
      <img
        src={src}
        alt=""
        loading="lazy"
        decoding="async"
        style={{ width: size, height: size }}
        className={cn('shrink-0 bg-surface-2 object-cover', r, className)}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      style={{ width: size, height: size, fontSize: Math.max(11, size * 0.3) }}
      className={cn('inline-flex shrink-0 items-center justify-center bg-primary-soft font-semibold tracking-[-0.02em] text-primary-strong', r, className)}
    >
      {initials(name)}
    </span>
  );
}
